// src/supabaseService.js
import { supabase, handleSupabaseError, getAccountId } from './supabaseClient';

// ============================================
// SETTINGS OPERATIONS
// ============================================

export const fetchSettings = async () => {
  try {
    console.log('🔍 [fetchSettings] Fetching settings...');
    const { data, error } = await supabase
      .from('settings')
      .select('*')
      .single();
    
    console.log('🔍 [fetchSettings] Query result:', { error, data, hasData: !!data });
    
    if (error) {
      console.error('❌ [fetchSettings] RLS or query error:', error);
      throw error;
    }
    
    if (!data) {
      console.warn('⚠️ [fetchSettings] No data returned (RLS may be denying access silently)');
      return { success: false, error: 'No settings found or access denied' };
    }
    
    const result = {
      volumeUnit: data.volume_unit,
      weightUnit: data.weight_unit,
      beerDensity: parseFloat(data.beer_density),
      maintenanceIntervals: data.maintenance_intervals
    };
    console.log('✅ [fetchSettings] Success:', result);
    return result;
  } catch (error) {
    console.error('❌ [fetchSettings] Exception:', error);
    return handleSupabaseError(error, 'fetchSettings');
  }
};

export const updateSettings = async (settings) => {
  try {
    // Get the settings ID first (there should only be one row)
    const { data: existingSettings } = await supabase
      .from('settings')
      .select('id')
      .limit(1)
      .single();
    
    if (!existingSettings) {
      throw new Error('No settings found');
    }
    
    const { data, error } = await supabase
      .from('settings')
      .update({
        volume_unit: settings.volumeUnit,
        weight_unit: settings.weightUnit,
        beer_density: settings.beerDensity,
        maintenance_intervals: settings.maintenanceIntervals
      })
      .eq('id', existingSettings.id)
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'updateSettings');
  }
};

// ============================================
// ACCOUNT OPERATIONS
// ============================================

export const fetchAccount = async () => {
  try {
    console.log('🔍 [fetchAccount] Fetching account data...');
    const { data: accountData, error: accountError } = await supabase
      .from('account')
      .select('*')
      .single();
    
    console.log('🔍 [fetchAccount] Account query result:', { accountError, hasData: !!accountData });
    
    if (accountError) {
      console.error('❌ [fetchAccount] RLS or query error:', accountError);
      throw accountError;
    }
    
    const { data: usersData, error: usersError } = await supabase
      .from('users')
      .select('*')
      .eq('account_id', accountData.id);
    
    console.log('🔍 [fetchAccount] Users query result:', { usersError, count: usersData?.length || 0 });
    
    if (usersError) {
      console.error('❌ [fetchAccount] Users query error:', usersError);
      throw usersError;
    }
    
    return {
      users: usersData.map(u => ({
        id: u.id,
        name: u.name,
        email: u.email,
        role: u.role
      })),
      qrPermissions: {
        publicAccess: accountData.public_access,
        editableWithoutAuth: accountData.editable_without_auth,
        viewableWithoutAuth: accountData.viewable_without_auth
      },
      profile: {
        name: accountData.name,
        ownerName: accountData.owner_name,
        defaultOwner: accountData.default_owner,
        logoUrl: accountData.logo_url
      },
      subscription: {
        plan: accountData.subscription_plan,
        status: accountData.subscription_status,
        nextBilling: accountData.next_billing
      }
    };
  } catch (error) {
    return handleSupabaseError(error, 'fetchAccount');
  }
};

export const updateAccount = async (account) => {
  try {
    const accountId = await getAccountId();
    
    const { data, error } = await supabase
      .from('account')
      .update({
        name: account.profile.name,
        owner_name: account.profile.ownerName,
        default_owner: account.profile.defaultOwner,
        logo_url: account.profile.logoUrl,
        public_access: account.qrPermissions.publicAccess,
        editable_without_auth: account.qrPermissions.editableWithoutAuth,
        viewable_without_auth: account.qrPermissions.viewableWithoutAuth
      })
      .eq('id', accountId)
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'updateAccount');
  }
};

// ============================================
// USER OPERATIONS
// ============================================

export const addUser = async (user) => {
  try {
    const accountId = await getAccountId();
    
    const { data, error } = await supabase
      .from('users')
      .insert({
        account_id: accountId,
        name: user.name,
        email: user.email,
        role: user.role
      })
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data: { id: data.id, ...user } };
  } catch (error) {
    return handleSupabaseError(error, 'addUser');
  }
};

export const updateUser = async (userId, user) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .update({
        name: user.name,
        email: user.email,
        role: user.role
      })
      .eq('id', userId)
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'updateUser');
  }
};

export const deleteUser = async (userId) => {
  try {
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', userId);
    
    if (error) throw error;
    return { success: true };
  } catch (error) {
    return handleSupabaseError(error, 'deleteUser');
  }
};

// ============================================
// LOCATION OPERATIONS
// ============================================

export const fetchLocations = async () => {
  try {
    const accountId = await getAccountId();
    
    const { data: locationsData, error: locationsError } = await supabase
      .from('locations')
      .select('*')
      .eq('account_id', accountId)
      .order('display_order');
    
    if (locationsError) throw locationsError;
    
    const locations = await Promise.all(
      locationsData.map(async (loc) => {
        const { data: sublocationsData, error: sublocationsError } = await supabase
          .from('sublocations')
          .select('*')
          .eq('location_id', loc.id)
          .order('display_order');
        
        if (sublocationsError) throw sublocationsError;
        
        return {
          id: loc.id,
          name: loc.name,
          description: loc.description,
          hasTaps: loc.has_taps,
          tapCount: loc.tap_count,
          tapNames: loc.tap_names ? JSON.parse(loc.tap_names) : [],
          sublocations: sublocationsData.map(sub => ({
            id: sub.id,
            name: sub.name,
            description: sub.description,
            hasTaps: sub.has_taps,
            tapCount: sub.tap_count,
            tapNames: sub.tap_names ? JSON.parse(sub.tap_names) : []
          }))
        };
      })
    );
    
    return locations;
  } catch (error) {
    return handleSupabaseError(error, 'fetchLocations');
  }
};

export const addLocation = async (location) => {
  try {
    const accountId = await getAccountId();
    
    const { data, error } = await supabase
      .from('locations')
      .insert({
        account_id: accountId,
        name: location.name,
        description: location.description,
        has_taps: !!location.hasTaps,
        tap_count: location.hasTaps ? location.tapCount : 0,
        tap_names: location.hasTaps ? JSON.stringify(location.tapNames) : null
      })
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data: { id: data.id, name: data.name, description: data.description, hasTaps: data.has_taps, tapCount: data.tap_count, tapNames: data.tap_names ? JSON.parse(data.tap_names) : [], sublocations: [] } };
  } catch (error) {
    return handleSupabaseError(error, 'addLocation');
  }
};

export const addSublocation = async (locationId, sublocation) => {
  try {
    const { data, error } = await supabase
      .from('sublocations')
      .insert({
        location_id: locationId,
        name: sublocation.name,
        description: sublocation.description,
        has_taps: !!sublocation.hasTaps,
        tap_count: sublocation.hasTaps ? sublocation.tapCount : 0,
        tap_names: sublocation.hasTaps ? JSON.stringify(sublocation.tapNames) : null
      })
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data: { id: data.id, name: data.name, description: data.description, hasTaps: data.has_taps, tapCount: data.tap_count, tapNames: data.tap_names ? JSON.parse(data.tap_names) : [] } };
  } catch (error) {
    return handleSupabaseError(error, 'addSublocation');
  }
};

export const deleteSublocation = async (sublocationId) => {
  try {
    const { error } = await supabase
      .from('sublocations')
      .delete()
      .eq('id', sublocationId);
    
    if (error) throw error;
    return { success: true };
  } catch (error) {
    return handleSupabaseError(error, 'deleteSublocation');
  }
};

export const deleteLocation = async (locationId) => {
  try {
    // Sublocations will be automatically deleted due to CASCADE
    const { error } = await supabase
      .from('locations')
      .delete()
      .eq('id', locationId);
    
    if (error) throw error;
    return { success: true };
  } catch (error) {
    return handleSupabaseError(error, 'deleteLocation');
  }
};

export const updateLocation = async (locationId, updates) => {
  try {
    const { data, error } = await supabase
      .from('locations')
      .update({
        name: updates.name,
        description: updates.description,
        has_taps: !!updates.hasTaps,
        tap_count: updates.hasTaps ? updates.tapCount : 0,
        tap_names: updates.hasTaps ? JSON.stringify(updates.tapNames) : null
      })
      .eq('id', locationId)
      .select();
    if (error) throw error;
    const d = data?.[0] || data;
    // Fetch sublocations to include in response
    const { data: sublocationsData } = await supabase
      .from('sublocations')
      .select('*')
      .eq('location_id', d.id)
      .order('display_order');
    return { success: true, data: { id: d.id, name: d.name, description: d.description, hasTaps: d.has_taps, tapCount: d.tap_count, tapNames: d.tap_names ? JSON.parse(d.tap_names) : [], sublocations: sublocationsData?.map(sub => ({ id: sub.id, name: sub.name, description: sub.description, hasTaps: sub.has_taps, tapCount: sub.tap_count, tapNames: sub.tap_names ? JSON.parse(sub.tap_names) : [] })) || [] } };
  } catch (error) {
    return handleSupabaseError(error, 'updateLocation');
  }
};

export const updateSublocation = async (sublocationId, updates) => {
  try {
    const { data, error } = await supabase
      .from('sublocations')
      .update({
        name: updates.name,
        description: updates.description,
        has_taps: !!updates.hasTaps,
        tap_count: updates.hasTaps ? updates.tapCount : 0,
        tap_names: updates.hasTaps ? JSON.stringify(updates.tapNames) : null
      })
      .eq('id', sublocationId)
      .select();
    if (error) throw error;
    const d = data?.[0] || data;
    return { success: true, data: { id: d.id, name: d.name, description: d.description, hasTaps: d.has_taps, tapCount: d.tap_count, tapNames: d.tap_names ? JSON.parse(d.tap_names) : [] } };
  } catch (error) {
    return handleSupabaseError(error, 'updateSublocation');
  }
};


// ============================================
// KEG OPERATIONS
// ============================================

export const fetchKegs = async (breweryId) => {
  try {
    // Use brewery_id when provided, but include legacy kegs scoped by account_id
    const accountId = await getAccountId();
    let query = supabase.from('kegs').select('*');

    if (breweryId) {
      // Include legacy rows that only have account_id for backwards compatibility
      query = query.or(`brewery_id.eq.${breweryId},and(brewery_id.is.null,account_id.eq.${accountId})`);
    } else {
      query = query.eq('account_id', accountId);
    }

    const { data: kegsData, error: kegsError } = await query.order('keg_number');
    
    if (kegsError) throw kegsError;
    
    // Fetch related data for each keg
    const kegs = await Promise.all(
      kegsData.map(async (keg) => {
        // Fetch location history
        const { data: locationHistoryData } = await supabase
          .from('location_history')
          .select('*')
          .eq('keg_id', keg.id)
          .order('moved_at', { ascending: false })
          .limit(5);
        
        // Fetch maintenance records
        const { data: maintenanceData } = await supabase
          .from('maintenance_records')
          .select('*')
          .eq('keg_id', keg.id)
          .order('maintenance_date', { ascending: false });
        
        // Fetch beer history with sessions and ratings
        const { data: beerHistoryData } = await supabase
          .from('beer_history')
          .select(`
            *,
            beer_sessions (
              *,
              ratings (*)
            )
          `)
          .eq('keg_id', keg.id)
          .order('created_at', { ascending: false });
        
        return {
          id: keg.id,
          uuid: keg.uuid,
          kegNumber: keg.keg_number,
          name: keg.name,
          owner: keg.owner,
          tapStatus: keg.tap_status,
          tapNumber: keg.tap_number,
          cleaningStatus: keg.cleaning_status,
          location: keg.location,
          sublocation: keg.sublocation,
          batchCount: keg.batch_count,
          isEmpty: keg.is_empty,
          currentBeer: keg.current_beer_name ? {
            name: keg.current_beer_name,
            style: keg.current_beer_style,
            abv: parseFloat(keg.current_beer_abv),
            ibu: keg.current_beer_ibu,
            og: parseFloat(keg.current_beer_og),
            fg: parseFloat(keg.current_beer_fg),
            description: keg.current_beer_description,
            imageUrl: keg.current_beer_image_url
          } : null,
          current_beer_started_at: keg.current_beer_started_at || null,
          kegDetails: {
            type: keg.keg_type,
            volume: parseFloat(keg.keg_volume),
            features: keg.keg_features || [],
            notes: keg.keg_notes
          },
          weights: {
            tare: keg.tare_weight ? parseFloat(keg.tare_weight) : null
          },
          maintenanceRecords: maintenanceData?.map(m => ({
            id: m.id,
            date: m.maintenance_date,
            items: m.items,
            notes: m.notes
          })) || [],
          locationHistory: locationHistoryData?.map(lh => ({
            location: lh.location,
            sublocation: lh.sublocation,
            date: new Date(lh.moved_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          })) || [],
          beerHistory: beerHistoryData?.map(bh => ({
            name: bh.name,
            style: bh.style,
            abv: parseFloat(bh.abv),
            ibu: bh.ibu,
            og: parseFloat(bh.og),
            fg: parseFloat(bh.fg),
            description: bh.description,
            imageUrl: bh.image_url,
            sessions: bh.beer_sessions?.map(session => ({
              kickedDate: session.kicked_date,
              location: session.location,
              tapStatus: session.tap_status,
              ratings: session.ratings?.map(r => ({
                rating: r.rating,
                notes: r.notes,
                raterName: r.rater_name,
                date: new Date(r.rated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                time: new Date(r.rated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
              })) || []
            })) || []
          })) || []
        };
      })
    );
    
    console.log('DEBUG: fetched kegs', kegs);
    return kegs;
  } catch (error) {
    return handleSupabaseError(error, 'fetchKegs');
  }
};

export const addKeg = async (keg, breweryId) => {
  try {
    const accountId = await getAccountId();
    const filterValue = breweryId || accountId;
    
    const { data, error } = await supabase
      .from('kegs')
      .insert({
        brewery_id: filterValue,
        account_id: accountId,
        uuid: keg.uuid,
        keg_number: keg.kegNumber,
        name: keg.name,
        owner: keg.owner,
        keg_type: keg.kegDetails.type,
        keg_volume: keg.kegDetails.volume,
        keg_features: keg.kegDetails.features,
        keg_notes: keg.kegDetails.notes,
        tare_weight: keg.weights.tare,
        location: keg.location,
        sublocation: keg.sublocation
      })
      .select()
      .single();
    
    if (error) throw error;
    
    // Add initial location history
    if (keg.location) {
      await supabase
        .from('location_history')
        .insert({
          keg_id: data.id,
          location: keg.location,
          sublocation: keg.sublocation
        });
    }
    
    return { success: true, data: { ...keg, id: data.id } };
  } catch (error) {
    return handleSupabaseError(error, 'addKeg');
  }
};

export const updateKeg = async (kegId, updates) => {
  try {
    const dbUpdates = {};
    
    // Map frontend fields to database fields
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.owner !== undefined) dbUpdates.owner = updates.owner;
    if (updates.tapStatus !== undefined) dbUpdates.tap_status = updates.tapStatus;
    if (updates.tapNumber !== undefined) dbUpdates.tap_number = updates.tapNumber;
    if (updates.cleaningStatus !== undefined) dbUpdates.cleaning_status = updates.cleaningStatus;
    if (updates.location !== undefined) dbUpdates.location = updates.location;
    if (updates.sublocation !== undefined) dbUpdates.sublocation = updates.sublocation;
    if (updates.batchCount !== undefined) dbUpdates.batch_count = updates.batchCount;
    if (updates.isEmpty !== undefined) dbUpdates.is_empty = updates.isEmpty;
    
    // Handle current beer updates
    if (updates.currentBeer !== undefined) {
      if (updates.currentBeer === null) {
        dbUpdates.current_beer_name = null;
        dbUpdates.current_beer_style = null;
        dbUpdates.current_beer_abv = null;
        dbUpdates.current_beer_ibu = null;
        dbUpdates.current_beer_og = null;
        dbUpdates.current_beer_fg = null;
        dbUpdates.current_beer_description = null;
        dbUpdates.current_beer_image_url = null;
      } else {
        dbUpdates.current_beer_name = updates.currentBeer.name;
        dbUpdates.current_beer_style = updates.currentBeer.style;
        dbUpdates.current_beer_abv = updates.currentBeer.abv;
        dbUpdates.current_beer_ibu = updates.currentBeer.ibu;
        dbUpdates.current_beer_og = updates.currentBeer.og;
        dbUpdates.current_beer_fg = updates.currentBeer.fg;
        dbUpdates.current_beer_description = updates.currentBeer.description;
        dbUpdates.current_beer_image_url = updates.currentBeer.imageUrl;
      }
    }
    
    // Handle keg details updates
    if (updates.kegDetails !== undefined) {
      if (updates.kegDetails.type !== undefined) dbUpdates.keg_type = updates.kegDetails.type;
      if (updates.kegDetails.volume !== undefined) dbUpdates.keg_volume = updates.kegDetails.volume;
      if (updates.kegDetails.features !== undefined) dbUpdates.keg_features = updates.kegDetails.features;
      if (updates.kegDetails.notes !== undefined) dbUpdates.keg_notes = updates.kegDetails.notes;
    }
    
    // Handle weights updates
    if (updates.weights !== undefined && updates.weights.tare !== undefined) {
      dbUpdates.tare_weight = updates.weights.tare;
    }
    
    const { data, error } = await supabase
      .from('kegs')
      .update(dbUpdates)
      .eq('id', kegId)
      .select();
    
    if (error) throw error;
    return { success: true, data: data?.[0] || data };
  } catch (error) {
    return handleSupabaseError(error, 'updateKeg');
  }
};

export const deleteKeg = async (kegId) => {
  try {
    const { error } = await supabase
      .from('kegs')
      .delete()
      .eq('id', kegId);
    
    if (error) throw error;
    return { success: true };
  } catch (error) {
    return handleSupabaseError(error, 'deleteKeg');
  }
};

// ============================================
// LOCATION HISTORY OPERATIONS
// ============================================

export const addLocationHistory = async (kegId, location, sublocation) => {
  try {
    const { data, error } = await supabase
      .from('location_history')
      .insert({
        keg_id: kegId,
        location,
        sublocation
      })
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'addLocationHistory');
  }
};

// ============================================
// BEER HISTORY OPERATIONS
// ============================================

export const addBeerHistory = async (kegId, beer, session) => {
  try {
    // First, add the beer to beer_history
    const { data: beerData, error: beerError } = await supabase
      .from('beer_history')
      .insert({
        keg_id: kegId,
        name: beer.name,
        style: beer.style,
        abv: beer.abv,
        ibu: beer.ibu,
        og: beer.og,
        fg: beer.fg,
        description: beer.description,
        image_url: beer.imageUrl
      })
      .select()
      .single();
    
    if (beerError) throw beerError;
    
    // Then, add the session
    const { data: sessionData, error: sessionError } = await supabase
      .from('beer_sessions')
      .insert({
        beer_history_id: beerData.id,
        kicked_date: session.kickedDate,
        location: session.location,
        tap_status: session.tapStatus,
        volume: session.volume || null
      })
      .select()
      .single();
    
    if (sessionError) throw sessionError;

    // Update keg to record when this beer was put on tap
    try {
      const { error: kegUpdateError } = await supabase
        .from('kegs')
        .update({ current_beer_started_at: session.kickedDate })
        .eq('id', kegId);
      if (kegUpdateError) console.warn('Failed to set current_beer_started_at:', kegUpdateError);
    } catch (e) {
      console.warn('Unexpected error setting current_beer_started_at:', e);
    }
    
    // Finally, add any ratings
    if (session.ratings && session.ratings.length > 0) {
      const ratingsToInsert = session.ratings.map(rating => ({
        beer_session_id: sessionData.id,
        rating: rating.rating,
        notes: rating.notes,
        rater_name: rating.raterName
      }));
      
      const { error: ratingsError } = await supabase
        .from('ratings')
        .insert(ratingsToInsert);
      
      if (ratingsError) throw ratingsError;
    }
    
    return { success: true, data: { beerHistoryId: beerData.id, sessionId: sessionData.id } };
  } catch (error) {
    return handleSupabaseError(error, 'addBeerHistory');
  }
};

export const endCurrentBeerSession = async (kegId, endedDate = new Date().toISOString(), volume = null) => {
  try {
    const { error } = await supabase.rpc('end_current_beer_session', { p_keg_id: kegId, p_ended_date: endedDate, p_volume: volume });
    if (error) throw error;
    return { success: true };
  } catch (error) {
    return handleSupabaseError(error, 'endCurrentBeerSession');
  }
};

export const addRating = async (kegId, rating) => {
  try {
    // Get the current beer's most recent session
    const { data: kegData } = await supabase
      .from('kegs')
      .select('id, current_beer_name')
      .eq('id', kegId)
      .single();
    
    if (!kegData || !kegData.current_beer_name) {
      throw new Error('No current beer on keg');
    }
    
    // Find the beer history entry for this beer
    const { data: beerHistoryData } = await supabase
      .from('beer_history')
      .select(`
        id,
        beer_sessions (id, kicked_date)
      `)
      .eq('keg_id', kegId)
      .eq('name', kegData.current_beer_name)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();
    
    if (!beerHistoryData || !beerHistoryData.beer_sessions || beerHistoryData.beer_sessions.length === 0) {
      throw new Error('No active session found for current beer');
    }
    
    // Get the most recent session (should be the active one)
    const activeSession = beerHistoryData.beer_sessions.sort((a, b) => 
      new Date(b.kicked_date) - new Date(a.kicked_date)
    )[0];
    
    // Add the rating to this session
    const { data, error } = await supabase
      .from('ratings')
      .insert({
        beer_session_id: activeSession.id,
        rating: rating.rating,
        notes: rating.notes,
        rater_name: rating.raterName
      })
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'addRating');
  }
};

// ============================================
// MAINTENANCE OPERATIONS
// ============================================

export const addMaintenanceRecord = async (kegId, maintenanceRecord) => {
  try {
    const { data, error } = await supabase
      .from('maintenance_records')
      .insert({
        keg_id: kegId,
        maintenance_date: maintenanceRecord.date,
        items: maintenanceRecord.items,
        notes: maintenanceRecord.notes
      })
      .select()
      .single();
    
    if (error) throw error;

    // Reset the keg's batch count after maintenance was performed
    const { error: resetError } = await supabase
      .from('kegs')
      .update({ batch_count: 0 })
      .eq('id', kegId);
    if (resetError) console.warn('Failed to reset keg batch count after maintenance:', resetError);

    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'addMaintenanceRecord');
  }
};

// Increment tap line usage for a given brewery/location/sublocation (called when a beer is put on tap)
export const incrementTapLineUsage = async (breweryId, location, sublocation = null, tapName = null) => {
  try {
    const { data, error } = await supabase
      .rpc('increment_tap_line_usage', { p_brewery_id: breweryId, p_location: location, p_sublocation: sublocation, p_tap_name: tapName });
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'incrementTapLineUsage');
  }
};

// Record location-level maintenance (e.g., Line Cleaning) and reset counters
export const recordLocationMaintenance = async (breweryId, location, sublocation = null, items = [], notes = '', tapNames = null) => {
  try {
    const { error } = await supabase
      .rpc('record_location_maintenance', { p_brewery_id: breweryId, p_location: location, p_sublocation: sublocation, p_items: items, p_notes: notes, p_tap_names: tapNames });
    if (error) throw error;
    return { success: true };
  } catch (error) {
    return handleSupabaseError(error, 'recordLocationMaintenance');
  }
};

// Record a brewery-level maintenance activity (e.g., Water Filtration changes)
// Optionally reset the water filtration batch counter
export const recordBreweryMaintenance = async (breweryId, items = [], notes = '', resetBatchCount = false) => {
  try {
    // Use location = 'Water Filtration' to keep records unified in location_maintenance_records
    const { data, error } = await supabase
      .from('location_maintenance_records')
      .insert({ brewery_id: breweryId, location: 'Water Filtration', sublocation: null, items, notes })
      .select()
      .single();

    if (error) throw error;

    // Reset the water filtration batch counter if requested
    if (resetBatchCount) {
      await resetWaterFiltrationBatch(breweryId);
    }

    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'recordBreweryMaintenance');
  }
};

// Increment the water filtration batch counter (called when beer is put on tap)
export const incrementWaterFiltrationBatch = async (breweryId) => {
  try {
    const { data, error } = await supabase
      .rpc('increment_water_filtration_batch', { p_brewery_id: breweryId });
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'incrementWaterFiltrationBatch');
  }
};

// Reset the water filtration batch counter (called during filter maintenance)
export const resetWaterFiltrationBatch = async (breweryId) => {
  try {
    const { error } = await supabase
      .rpc('reset_water_filtration_batch', { p_brewery_id: breweryId });
    if (error) throw error;
    return { success: true };
  } catch (error) {
    return handleSupabaseError(error, 'resetWaterFiltrationBatch');
  }
};

// Fetch the current water filtration batch count for a brewery
export const fetchWaterFiltrationBatchCount = async (breweryId) => {
  try {
    const { data, error } = await supabase
      .from('breweries')
      .select('water_filtration_batch_count')
      .eq('id', breweryId)
      .single();
    if (error) throw error;
    return { success: true, data: data?.water_filtration_batch_count || 0 };
  } catch (error) {
    return handleSupabaseError(error, 'fetchWaterFiltrationBatchCount');
  }
};

// Fetch tap line statuses for a brewery
export const fetchTapLineStatuses = async (breweryId) => {
  try {
    const { data, error } = await supabase
      .from('tap_line_status')
      .select('*')
      .eq('brewery_id', breweryId)
      .order('location', { ascending: true });
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'fetchTapLineStatuses');
  }
};

// Fetch recent location maintenance records for a brewery
export const fetchLocationMaintenanceRecords = async (breweryId, limit = 100) => {
  try {
    const { data, error } = await supabase
      .from('location_maintenance_records')
      .select('*')
      .eq('brewery_id', breweryId)
      .order('maintenance_date', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'fetchLocationMaintenanceRecords');
  }
};

// ============================================
// BULK OPERATIONS
// ============================================

export const bulkUpdateKegs = async (kegIds, updates) => {
  try {
    const results = await Promise.all(
      kegIds.map(id => updateKeg(id, updates))
    );
    
    const hasErrors = results.some(r => !r.success);
    if (hasErrors) {
      return { success: false, error: 'Some updates failed' };
    }
    
    return { success: true };
  } catch (error) {
    return handleSupabaseError(error, 'bulkUpdateKegs');
  }
};

// ============================================
// PROFILE OPERATIONS
// ============================================

/**
 * Fetch the current user's profile
 * @returns {{ success: boolean, data?: object, error?: string }}
 */
export const getProfile = async () => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .single();
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'getProfile');
  }
};

/**
 * Update the current user's profile
 * @param {object} updates - { name }
 * @returns {{ success: boolean, data?: object, error?: string }}
 */
export const updateProfile = async (updates) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        name: updates.name,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', (await supabase.auth.getUser()).data.user.id)
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'updateProfile');
  }
};

/**
 * Get profile by user ID (for invites, team members, etc.)
 * @param {string} userId 
 * @returns {{ success: boolean, data?: object, error?: string }}
 */
export const getProfileByUserId = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .single();
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return handleSupabaseError(error, 'getProfileByUserId');
  }
};